import { useState, useEffect, useRef } from "react";
import { useSession } from "next-auth/react";
import { Flex, Text, Button, Box, useColorModeValue } from "@chakra-ui/react";
import { AiOutlineEye } from "react-icons/ai";
import toast from "react-hot-toast";
import { useOnClickOutside } from "utils/helpers";
import LinkedinPost from "components/tweets/masonry/linkedinPost";
import { formatTweetFromPost } from "../../utils/linkedinUtils";
import { textStyle } from "theme/names";
import { TweetLabels, Label } from "components/tweet-labels";
import { getAccount } from "utils/sessionHelper";
import { useFirebaseUser } from "utils/useFirebaseUser";
import { useMiscellaneousContext } from "context/miscellaneousContext";
import { getToken } from "firebaseClient";

export function PreviewText({ value }) {
  const [labels, setLabels] = useState<Label[]>([]);
  const [isTweetOpen, setIsTweetOpen] = useState(false);
  const [selectedLabelIds, setSelectedLabelIds] = useState<string[]>([]);
  const refTweet = useRef<any>();
  const { data: session } = useSession() ?? {};
  const isAuthenticated = useFirebaseUser();
  const miscellaneousContext: any = useMiscellaneousContext();

  useOnClickOutside(refTweet, async () => {
    await new Promise((resolve) => setTimeout(resolve, 200));
    setIsTweetOpen(false);
  });

  useEffect(() => {
    if (session && isAuthenticated) {
      const labels = getAccount(session)?.tweetLabels ?? [];
      setLabels([...labels]);
    }
  }, [session, isAuthenticated, miscellaneousContext.refreshLabels]);

  useEffect(() => {
    setSelectedLabelIds(value.tweet?.labels ?? []);
  }, []);

  const handleLabelsChange = async (labels: string[], callback) => {
    try {
      if (!isAuthenticated) {
        return;
      }

      setSelectedLabelIds(labels);
      if (value.tweet.labels) {
        value.tweet.labels = labels;
      }
      callback && callback();

      const payload = {
        tokenUserId: session?.user.uid,
        token: await getToken(session, "statsLabels"),
        post: value.tweet,
        labels
      };

      const respone = await fetch("/api/statsLabels", {
        method: "POST",
        headers: {
          "Content-Type": "application/json"
        },
        body: JSON.stringify(payload)
      })
      const data = await respone.json();

      if (!data.success) {
        console.error(data.message);
        toast.error("Error in updating labels: ", data.message);
        setSelectedLabelIds(value.tweet?.labels ?? []);
      }
    } catch (err) {
      console.log("Error in updating stat: ", err);
      toast.error("Error in updating stat: " + err.message);
      setSelectedLabelIds(value.tweet?.labels ?? []);
    }
  };

  const hasLabels = selectedLabelIds?.length > 0 && labels.filter((l) => selectedLabelIds.includes(l.id)).length > 0;
  const bgWhite = useColorModeValue("white", "gray.800");

  return (
    <Flex alignItems={hasLabels ? "start" : "center"} justifyContent="space-between" position="relative">
      <Flex
        alignItems={hasLabels ? "start" : "center"}
        flexDirection={hasLabels ? "column" : "row"} 
        justifyContent="space-between"
        style={{
          width:(hasLabels)? "100%" : "calc(100% - 57px"
        }}
      >
        <Text
          textStyle={textStyle["body.medium.light"]}
         
          minW="100%"
          maxW="320px"
          textAlign="start"
          noOfLines={1}
          wordBreak={"break-all"}
          isTruncated
        >
          {value.text}
        </Text>
        <Box minW={hasLabels ? "100%" : ""} maxW={hasLabels ? "320px" : "100%"}>
          <TweetLabels
            size="xs"
            maxLabelsToDisplay={1}
            showOnlySelectedLabels={true}
            showSelectedLabelsOnSearch={true}
            mainContainerProps={{
              mt: "1",
              width:"100%",
              justify:"space-between"
            }}
            addButtonPadding={false}
            selectedLabelIds={selectedLabelIds}
            onChange={handleLabelsChange}
          />
        </Box>
      </Flex>
      <Button
        background="none"
        size="xs"
        onClick={() => {
          setIsTweetOpen(true);
        }}
      >
        <AiOutlineEye />
      </Button>
      {isTweetOpen && (
        <Box
          position="absolute"
          top="40px"
          right="0"
          minW="315px"
          bg={bgWhite}
          boxShadow="rgba(100, 100, 111, 0.15) 0px 2px 19px 0px;"
          rounded="md"
          zIndex="1"
          ref={refTweet}
          fontSize="md"
          textAlign="start"
        >
          {/* 
          // @ts-ignore */}
          <LinkedinPost tweet={{ data: formatTweetFromPost(value.tweet) }} />
        </Box>
      )}
    </Flex>
  );
}
